import type { InboxConversation } from "@/hooks/use-inbox-conversations"

// Attribute filters for the messages inbox, applied client-side over the loaded conversations BEFORE the
// status chips and counts. AND across facets, OR within a facet; an empty facet means no restriction.
export interface InboxAttributeFilters {
  /** Inclusive local calendar days, "yyyy-mm-dd" as produced by <input type="date">; null = unbounded. */
  from: string | null
  to: string | null
  /** Work-order stage values (Assignment rows). */
  stages: string[]
  /** Request family labels (request rows' `stage`): "ATS" | "VMS" | "Client Request" | "Onboarding". */
  requestTypes: string[]
  /** Latest-message author names (`previewAuthor`). */
  senders: string[]
  /** Client names (`clientName`). */
  clients: string[]
}

export const EMPTY_ATTRIBUTE_FILTERS: InboxAttributeFilters = { from: null, to: null, stages: [], requestTypes: [], senders: [], clients: [] }

export function hasActiveAttributeFilters(f: InboxAttributeFilters): boolean {
  return (
    f.from !== null ||
    f.to !== null ||
    f.stages.length > 0 ||
    f.requestTypes.length > 0 ||
    f.senders.length > 0 ||
    f.clients.length > 0
  )
}

/** True when a filter that depends on enriched metadata (stages, requestTypes, clients) is active. */
export function needsMetadata(f: InboxAttributeFilters): boolean {
  return f.stages.length > 0 || f.requestTypes.length > 0 || f.clients.length > 0
}

// Local-day bounds: `to` is exclusive at the start of the NEXT local day (setDate, not +86400000, which is
// wrong on DST days).
function startOfDay(day: string): number {
  return new Date(`${day}T00:00:00`).getTime()
}

function startOfNextDay(day: string): number {
  const end = new Date(`${day}T00:00:00`)
  end.setDate(end.getDate() + 1)
  return end.getTime()
}

function matchesDate(row: InboxConversation, from: string | null, to: string | null): boolean {
  if (from === null && to === null) return true
  const at = row.lastActivity ? Date.parse(row.lastActivity) : NaN
  if (Number.isNaN(at)) return false
  if (from !== null && !(at >= startOfDay(from))) return false
  if (to !== null && !(at < startOfNextDay(to))) return false
  return true
}

export function matchesAttributeFilters(row: InboxConversation, f: InboxAttributeFilters): boolean {
  if (!matchesDate(row, f.from, f.to)) return false
  // Stage + request types are ONE facet: a row matches against its own group only.
  if (f.stages.length > 0 || f.requestTypes.length > 0) {
    const group = row.isRequestRow ? f.requestTypes : f.stages
    if (!group.includes(row.stage)) return false
  }
  if (f.senders.length > 0 && !f.senders.includes(row.previewAuthor)) return false
  if (f.clients.length > 0 && !f.clients.includes(row.clientName)) return false
  return true
}

// WO # search, local over the enriched rows. Prefix match (not substring) so a 1–2 digit term doesn't hit
// almost every WO. Accepts "67804", "#67804" and "WO 67804". The "WO" prefix is only stripped before a digit,
// so a name like "Wolfgang" isn't tested as WO "lfgang".
export function matchesWorkOrderNumber(row: InboxConversation, term: string): boolean {
  const normalised = term.trim().toLowerCase().replace(/^#|^wo\s*(?=\d)/, "")
  if (!normalised) return false
  return row.fboWorkOrderNumber.toLowerCase().startsWith(normalised)
}

export function collectFilterOptions(rows: InboxConversation[]): { stages: string[]; requestTypes: string[]; senders: string[]; clients: string[] } {
  const stages = new Set<string>()
  const requestTypes = new Set<string>()
  const senders = new Set<string>()
  const clients = new Set<string>()
  for (const r of rows) {
    if (r.stage) (r.isRequestRow ? requestTypes : stages).add(r.stage)
    if (r.previewAuthor) senders.add(r.previewAuthor)
    if (r.clientName) clients.add(r.clientName)
  }
  const sorted = (s: Set<string>) => Array.from(s).sort((a, b) => a.localeCompare(b))
  return { stages: sorted(stages), requestTypes: sorted(requestTypes), senders: sorted(senders), clients: sorted(clients) }
}
